/**
 * useIdlePrefetch Hook
 * Prefetches routes during browser idle time to improve navigation performance
 */

import { useEffect } from 'react';
import { prefetchRoutes } from '@/utils/routePrefetch';

// Priority routes to prefetch first (most commonly visited)
const PRIORITY_ROUTES = [
  '/dashboard',
  '/vehicles',
  '/customers',
  '/sales-invoices',
  '/purchase-invoices',
];

// Secondary routes to prefetch after priority routes
const SECONDARY_ROUTES = [
  '/pipeline',
  '/calendar',
  '/analytics',
  '/settings',
];

/**
 * Prefetch routes during browser idle time
 * Uses requestIdleCallback for optimal performance
 */
export function useIdlePrefetch() {
  useEffect(() => {
    // Check if requestIdleCallback is supported
    if (typeof window === 'undefined' || !('requestIdleCallback' in window)) {
      return;
    }

    // Prefetch priority routes first
    const priorityHandle = requestIdleCallback(
      () => {
        prefetchRoutes(PRIORITY_ROUTES);
      },
      { timeout: 2000 } // Max 2s wait
    );

    // Prefetch secondary routes after priority
    const secondaryHandle = requestIdleCallback(
      () => {
        prefetchRoutes(SECONDARY_ROUTES);
      },
      { timeout: 5000 } // Max 5s wait
    );

    // Cleanup
    return () => {
      if (priorityHandle) cancelIdleCallback(priorityHandle);
      if (secondaryHandle) cancelIdleCallback(secondaryHandle);
    };
  }, []); // Run once on mount
}
